export interface WatchTracker {
  recordTime(currentTime: number): void
  getUnsentBuckets(): number[]
  markBucketsSent(buckets: number[]): void
  reset(): void
}

const BUCKET_SIZE = 5
const TIME_TOLERANCE = 2
const MIN_WATCH_FRACTION = 0.5

export function createWatchTracker(): WatchTracker {
  let lastReportedTime: number | null = null
  let bucketProgress = new Map<number, number>()
  let pendingBuckets = new Set<number>()
  let sentBuckets = new Set<number>()

  function bucketForTime(time: number): number {
    return Math.floor(time / BUCKET_SIZE)
  }

  function recordTime(currentTime: number): void {
    if (lastReportedTime === null) {
      lastReportedTime = currentTime
      return
    }

    const timeDelta = currentTime - lastReportedTime

    if (timeDelta > TIME_TOLERANCE + 1) {
      lastReportedTime = currentTime
      return
    }

    if (timeDelta < -TIME_TOLERANCE) {
      lastReportedTime = currentTime
      return
    }

    if (timeDelta > 0 && timeDelta <= TIME_TOLERANCE + 1) {
      const currentBucket = bucketForTime(currentTime)
      const progress = bucketProgress.get(currentBucket) || 0
      bucketProgress.set(currentBucket, progress + timeDelta)

      if (bucketProgress.get(currentBucket)! >= BUCKET_SIZE * MIN_WATCH_FRACTION) {
        if (!sentBuckets.has(currentBucket)) {
          pendingBuckets.add(currentBucket)
        }
        bucketProgress.delete(currentBucket)
      }
    }

    lastReportedTime = currentTime
  }

  function getUnsentBuckets(): number[] {
    return Array.from(pendingBuckets).sort((a, b) => a - b)
  }

  function markBucketsSent(buckets: number[]): void {
    buckets.forEach(b => {
      pendingBuckets.delete(b)
      sentBuckets.add(b)
    })
  }

  function reset(): void {
    lastReportedTime = null
    bucketProgress.clear()
    pendingBuckets.clear()
  }

  return { recordTime, getUnsentBuckets, markBucketsSent, reset }
}
